import { Barcode } from '../../components/barcode.js';
import { entryModeWords } from '@sitzmark/core';
import { useQuery } from '@tanstack/react-query';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../../lib/api.js';
import { money } from '../../lib/money.js';
import { TENDER_LABELS } from '../../lib/tender.js';
import './receipt-print.css';

/**
 * The printed receipt (SCOPE §8.6).
 *
 * Rendered as HTML with a print stylesheet rather than raw printer commands.
 * Three reasons: it prints on the ordinary office printer a shop has *before*
 * the thermal unit arrives, the layout can be approved before any hardware is
 * bought, and it is the same document whether it goes to paper, a PDF, or
 * email. An ESC/POS driver slots in behind the same adapter later (§12.4)
 * without the layout changing.
 *
 * Width is per-tenant config: 80mm is the retail standard, 58mm exists on
 * smaller units, `letter` is the office printer.
 */

/** "visa" → "Visa", as a card network writes its own name. */
function cardBrandWord(brand: string): string {
  return brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase();
}

export function useReceiptPrinting() {
  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.settings.get.query(),
    staleTime: 5 * 60_000,
  });

  return settings.data ?? null;
}

type SaleDetail = Awaited<ReturnType<typeof api.sales.get.query>>;
type Settings = Awaited<ReturnType<typeof api.settings.get.query>>;

export function ReceiptDocument({
  detail,
  settings,
  giftReceipt = false,
}: {
  detail: SaleDetail;
  settings: Settings;
  /** A gift receipt proves purchase without showing what was paid (§8.6). */
  giftReceipt?: boolean;
}) {
  const isRefund = detail.sale.originalSaleId !== null;
  /** Half of a swap, rather than a return or a sale standing on its own. */
  const isExchange = detail.exchange != null;
  const store = settings.store;
  const receipt = detail.receipt;

  return (
    <div className={`receipt-doc receipt-doc--${settings.receipt.width}`}>
      <header className="receipt-doc__head">
        <div className="receipt-doc__store">{store.legalName ?? store.name}</div>
        {store.addressLine1 !== null ? <div>{store.addressLine1}</div> : null}
        {store.city !== null ? (
          <div>
            {store.city}
            {store.region !== null ? `, ${store.region}` : ''} {store.postalCode ?? ''}
          </div>
        ) : null}
        {store.phone !== null ? <div>{store.phone}</div> : null}
        {settings.receipt.header !== null ? (
          <div className="receipt-doc__message">{settings.receipt.header}</div>
        ) : null}
      </header>

      <div className="receipt-doc__meta">
        <div>
          {/*
            An exchange says so on both halves (§8.5). The customer is handed
            one or both of these documents, and a "Refund" for $348.10 beside a
            "Sale" for $348.10 with an unexplained credit line reads like two
            transactions — which is exactly the impression the exchange model
            exists to avoid.
          */}
          <span>
            {isExchange
              ? isRefund
                ? 'Exchange — returned'
                : 'Exchange — taken'
              : isRefund
                ? 'Refund'
                : 'Sale'}
          </span>
          <span className="receipt-doc__code">{detail.sale.number ?? '—'}</span>
        </div>
        {isExchange ? (
          <div>
            <span>{detail.exchange!.isRefund ? 'Returned on' : 'Taken on'}</span>
            <span className="receipt-doc__code">{detail.exchange!.number ?? '—'}</span>
          </div>
        ) : null}
        <div>
          <span>
            {detail.sale.completedAt === null
              ? ''
              : new Date(detail.sale.completedAt).toLocaleString()}
          </span>
        </div>
        <div>
          <span>{detail.sale.registerName ?? ''}</span>
          <span>{detail.sale.employeeName ?? ''}</span>
        </div>
      </div>

      {/*
        The receipt is written in whichever posted price was actually charged
        (decision 39). Every figure below — line prices, discount, tax, total —
        comes from `detail.receipt`, which the server has already resolved to
        one basis.

        **Nothing says which basis.** There is no "CASH PRICE" banner and no
        second column, because the customer is not being asked to compare two
        prices — they are being shown what they paid. The only statement about
        method is "Paid by ... Card" at the bottom, which is a fact about the
        payment rather than a caveat about the prices. A receipt that announces
        its own pricing basis invites the question the two-posted-prices model
        exists to avoid.
      */}
      <table className="receipt-doc__lines">
        <tbody>
          {receipt.lines.map((line) => (
            <tr key={line.id}>
              <td>
                <div>
                  {line.description}
                  {/* Which sentence in the footer covers this line (item 14).
                      Printed when the basket mixes both kinds — on a uniform
                      basket the single policy block says it all — and whenever
                      the store has no final-sale wording, when the mark is the
                      only thing saying so (a consigned item, decision 139). */}
                  {line.finalSale &&
                  (receipt.lines.some((other) => !other.finalSale) ||
                    settings.receipt.finalSalePolicy === null) ? (
                    <span className="receipt-doc__final-sale"> · FINAL SALE</span>
                  ) : null}
                </div>
                <div className="receipt-doc__sku">{line.sku ?? ''}</div>
                {/* A gift card sold with no card: this paper is the card, so it
                    carries the whole number and bars to scan it (FB-1106) — on
                    a gift receipt too, which may be what is handed over. */}
                {line.giftCardCode !== null ? (
                  <div className="receipt-doc__gift-card">
                    <div>Gift card number — keep this receipt</div>
                    <Barcode value={line.giftCardCode} className="receipt-doc__bars" />
                    <span className="receipt-doc__barcode-number">
                      {line.giftCardCode.replace(/(\d{4})(?=\d)/g, '$1 ')}
                    </span>
                  </div>
                ) : null}
                {!giftReceipt ? (
                  <div className="receipt-doc__qty">
                    {Math.abs(line.quantity)} @ {money(line.unitPriceCents)}
                    {line.discountCents !== 0 ? ` less ${money(line.discountCents)}` : ''}
                  </div>
                ) : (
                  <div className="receipt-doc__qty">Qty {Math.abs(line.quantity)}</div>
                )}
              </td>
              {!giftReceipt ? (
                <td className="receipt-doc__amount">{money(line.netCents)}</td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>

      {!giftReceipt ? (
        <div className="receipt-doc__totals">
          <Row label="Subtotal" value={money(receipt.subtotalCents)} />
          {receipt.discountCents !== 0 ? (
            <Row label="Discounts" value={money(-receipt.discountCents)} />
          ) : null}
          {receipt.taxes.map((tax) => (
            <Row key={tax.name} label={tax.name} value={money(tax.taxCents)} />
          ))}
          <Row label={isRefund ? 'Refunded' : 'Total'} value={money(receipt.totalCents)} strong />

          {/*
            "Paid by" and then the method. That is the whole disclosure: the
            figures above are what was charged, and this says how. No pricing
            language anywhere on the document.
          */}
          <div className="receipt-doc__tenders">
            {/* Money going back out is not "paid by" anything. */}
            <div className="receipt-doc__paid-by">{isRefund ? 'Refunded to' : 'Paid by'}</div>
            {detail.tenders
              .filter((tender) => tender.voidedAt === null)
              .map((tender) => (
                <Fragment key={tender.id}>
                  <Row
                    label={`${TENDER_LABELS[tender.kind] ?? tender.kind}${
                      tender.cardBrand !== null ? ` ${cardBrandWord(tender.cardBrand)}` : ''
                    }${tender.cardLast4 !== null ? ` ····${tender.cardLast4}` : ''}`}
                    value={money(tender.amountCents)}
                  />
                  {/* A card through BlueSage: how it was read and its approval (decision 230). */}
                  {tender.authCode !== null ? (
                    <Row
                      label={[entryModeWords(tender.entryMode), `Approval ${tender.authCode}`]
                        .filter((part) => part !== null)
                        .join(' · ')}
                      value=""
                    />
                  ) : null}
                </Fragment>
              ))}
            {detail.tenders.some((tender) => tender.changeGivenCents > 0) ? (
              <Row
                label="Change"
                value={money(
                  detail.tenders.reduce((sum, tender) => sum + tender.changeGivenCents, 0),
                )}
              />
            ) : null}
          </div>
        </div>
      ) : null}

      <footer className="receipt-doc__foot">
        {/*
          One sentence, because an exchange credit on its own explains nothing.
          It is the difference between a customer reading "Exchange credit
          $348.10" and wondering what was charged, and reading that only the
          difference moved (§2.2-10 — every derived figure explains itself).
        */}
        {isExchange && !giftReceipt ? (
          <div className="receipt-doc__message">
            Exchanged against {detail.exchange!.isRefund ? 'return' : 'sale'}{' '}
            {detail.exchange!.number ?? ''}. Only the difference changed hands.
          </div>
        ) : null}
        {/*
          The receipt barcode encodes the sale number, so a return starts with a
          scan rather than a search (§8.6).

          **Real Code 128 bars, drawn as SVG rectangles** — the same component
          the labels use, and for the same reason (decision 29). This printed
          the number wrapped in asterisks for months, waiting on "the barcode
          font that ships with the thermal driver", which was never installed
          and could not be: a font-based barcode prints only where the font is,
          and when it is not there the receipt looks finished and is
          unscannable. Nothing on a Sitzmark receipt could be scanned until now,
          which is the thing that had to be true before returns can require one.
        */}
        {detail.sale.number !== null ? (
          <div className="receipt-doc__barcode">
            <Barcode value={detail.sale.number} className="receipt-doc__bars" />
            {/* The number below the bars, always. A scanner reads the bars; a
                person reads this when the roll has smudged or the scanner is
                across the shop, and a return must not depend on hardware. */}
            <span className="receipt-doc__barcode-number">{detail.sale.number}</span>
          </div>
        ) : null}
        {/*
          Two policies, printed by what is actually in the basket (item 14).
          A uniform basket gets one block with no heading — the sentence
          speaks for the whole receipt. A mixed basket gets both, each under
          the heading that says which lines it covers, and the covered lines
          carry the FINAL SALE mark above so the customer can pair sentence
          to item without asking.
        */}
        {(() => {
          const anyFinal = receipt.lines.some((line) => line.finalSale);
          const anyStandard = receipt.lines.some((line) => !line.finalSale);
          const mixed = anyFinal && anyStandard;
          return (
            <>
              {anyStandard && settings.receipt.returnPolicy !== null ? (
                <div className="receipt-doc__policy">
                  {mixed ? <span className="receipt-doc__policy-label">Returns · </span> : null}
                  {settings.receipt.returnPolicy}
                </div>
              ) : null}
              {anyFinal && settings.receipt.finalSalePolicy !== null ? (
                <div className="receipt-doc__policy">
                  {mixed ? (
                    <span className="receipt-doc__policy-label">Final sale items · </span>
                  ) : null}
                  {settings.receipt.finalSalePolicy}
                </div>
              ) : null}
            </>
          );
        })()}
        {settings.receipt.footer !== null ? (
          <div className="receipt-doc__message">{settings.receipt.footer}</div>
        ) : null}
      </footer>
    </div>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={`receipt-doc__row${strong === true ? ' receipt-doc__row--strong' : ''}`}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

/**
 * Print a receipt.
 *
 * Renders the document into `document.body` via a portal, forces React to
 * commit it synchronously, then calls `window.print()` — all inside the click
 * handler.
 *
 * The obvious approach (mount a component, print from its effect) is broken in
 * two ways that both bite silently. StrictMode double-invokes effects, so a
 * `useRef` guard plus a `clearTimeout` cleanup cancels the only scheduled print
 * and the second run short-circuits on the ref — the button does nothing at
 * all, with no error. And the sales receipt is rendered inside a `<dialog>`,
 * whose top-layer rendering makes printing unreliable; the portal takes it out
 * of there entirely.
 */
/**
 * Paper size per receipt width.
 *
 * `@page` is a document-level rule and cannot be selected by class, so the
 * right one is injected for the duration of the print. Without it an 80mm
 * receipt goes onto whatever the printer's default paper is — a letter sheet
 * with a narrow ribbon of text down one side.
 *
 * `auto` height gives thermal printers a continuous receipt rather than
 * cutting at an arbitrary page boundary.
 */
const PAGE_RULES: Readonly<Record<string, string>> = {
  '80mm': '@page { size: 80mm auto; margin: 2mm; }',
  '58mm': '@page { size: 58mm auto; margin: 2mm; }',
  letter: '@page { size: letter; margin: 12mm; }',
};

/**
 * Print a receipt, from an event handler **or from an effect**.
 *
 * The original shape called `flushSync` to commit the document and then
 * `window.print()` on the next line. That is correct from a click handler and
 * **silently wrong from an effect**: React will not flush while it is already
 * rendering, so the update was deferred, the browser was asked to print an
 * empty portal, and the customer got a blank page. Reported the day auto-print
 * shipped (revision item 8) — the print dialog opened, correctly, over nothing.
 *
 * So the print moved into an effect of its own. The document is committed by an
 * ordinary state update, and only once React has actually put it in the DOM
 * does the browser get asked to print it. That holds whatever called `print()`,
 * which is the property the old shape lacked.
 *
 * The other three printers — labels, the purchase order, the receiving document
 * — still use the older `flushSync` shape. That is safe today because each is
 * only ever reached from a button, and this comment is here so the next person
 * to print one automatically knows what to change.
 */
export function useReceiptPrinter() {
  const [document_, setDocument] = useState<{
    detail: SaleDetail;
    settings: Settings;
    giftReceipt: boolean;
  } | null>(null);

  const print = useCallback((detail: SaleDetail, settings: Settings, giftReceipt = false) => {
    setDocument({ detail, settings, giftReceipt });
  }, []);

  useEffect(() => {
    if (document_ === null) return;

    /*
     * `@page` is a document-level rule and cannot be selected by class, so the
     * right one is injected for the duration of the print and removed after.
     */
    const style = document.createElement('style');
    style.textContent = PAGE_RULES[document_.settings.receipt.width] ?? PAGE_RULES.letter!;
    document.head.appendChild(style);

    try {
      window.print();
    } finally {
      style.remove();
      // Clearing this is what takes the portal back out of the DOM.
      setDocument(null);
    }
  }, [document_]);

  const frame =
    document_ === null
      ? null
      : createPortal(
          <div className="print-only">
            <ReceiptDocument
              detail={document_.detail}
              settings={document_.settings}
              giftReceipt={document_.giftReceipt}
            />
          </div>,
          document.body,
        );

  return { print, frame };
}
